import React, { useMemo } from 'react';
import {
  Link as BaseLink,
  LinkProps as BaseLinkProps,
  NavLink as BaseNavLink,
  NavLinkProps as BaseNavLinkProps,
} from 'react-router-dom';
import { useGlobalQueryString } from '../../hooks/useGlobalFilter';
import { themeColor } from '@hud/web-components/theme/colors';
import styled from 'styled-components';

type LinkProps = Omit<BaseLinkProps, 'to'> & {
  propagateGlobalFilters?: boolean;
  to: string;
  transparent?: boolean;
};

export const Link: React.FC<LinkProps> = ({
  propagateGlobalFilters = true,
  to,
  ...props
}: LinkProps) => {
  const globalFilters = useGlobalQueryString();

  const toWithGlobalFilters = useMemo(() => {
    if (!propagateGlobalFilters) {
      return to;
    }
    if (to.includes('?')) {
      return `${to}&${globalFilters}`;
    }

    return `${to}?${globalFilters}`;
  }, [to, propagateGlobalFilters, globalFilters]);

  if (props.transparent) {
    return <TransparentLink {...props} to={toWithGlobalFilters} />;
  }

  return <BaseLink {...props} to={toWithGlobalFilters} />;
};

type NavLinkProps = Omit<BaseNavLinkProps, 'to'> & {
  propagateGlobalFilters?: boolean;
  to: string;
};

export const NavLink = ({
  propagateGlobalFilters = true,
  to,
  ...props
}: NavLinkProps) => {
  const globalFilters = useGlobalQueryString();

  // toWithGlobalFilters = 'add globa filters string to 'to' prop'. it might not have '?' or '&' in it, it also might have search params already
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const toWithGlobalFilters = useMemo(() => {
    if (!propagateGlobalFilters) {
      return to;
    }
    if (to.includes('?')) {
      return `${to}&${globalFilters}`;
    }

    return `${to}?${globalFilters}`;
  }, [to, propagateGlobalFilters, globalFilters]);

  return <BaseNavLink {...props} to={toWithGlobalFilters} />;
};

const TransparentLink = styled(BaseLink)`
  color: ${themeColor('textPrimary')};
  text-decoration: none;
`;
